import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { readAllChatMessages } from "../../api/api";
import { defaultPfpUrl, defaultGroupPfpUrl, formatRelativeTimestamp, isUserOnline } from "../utils";
import { Avatar, AvatarFallback, AvatarImage } from "../ui/avatar";
import { Badge } from "../ui/badge";
import { ScrollArea } from "../ui/scroll-area";
import { Skeleton } from "../ui/skeleton";
import { cn } from "../../lib/utils";
import HighlightedText from "./HighlightedText";
import { useNow } from "../../hooks/useNow";
import { useUserRealtime } from "../../context/UserRealtimeContext";
import { memberLastOnline } from "./chat_frame/presenceUtils";

/**
 * Overlapping member avatars for group chats.
 * @param {{ chat: { other_members?: Array<{ user_id?: number, name?: string, pfp_url?: string, pfp?: string }>, image_url?: string, name: string }, n: number }} props
 * @returns {JSX.Element}
 */
function GroupChatPfp({ chat, n }) {
  const members = chat.other_members?.slice(0, n) || [];

  if (members.length === 0) {
    return (
      <Avatar className="mr-3 h-12 w-12 shrink-0">
        <AvatarImage
          src={chat.image_url || defaultGroupPfpUrl}
          alt={chat.name}
          onError={(e) => {
            e.target.src = defaultGroupPfpUrl;
          }}
        />
        <AvatarFallback className="text-xs">
          {chat.name?.slice(0, 2)?.toUpperCase() || "?"}
        </AvatarFallback>
      </Avatar>
    );
  }

  const containerSize = 48;
  const imageSize = 32;
  const centerX = containerSize / 2;
  const centerY = containerSize / 2;
  const radius = 13;

  return (
    <div
      className="relative mr-3 shrink-0"
      style={{ width: `${containerSize}px`, height: `${containerSize}px` }}
    >
      {members.map((member, index) => {
        const pfpUrl = member.pfp_url || member.pfp || defaultPfpUrl;
        const zIndex = index + 1;
        const angle =
          (index * 2 * Math.PI) / members.length - Math.PI / 2;
        const x = centerX + radius * Math.cos(angle) - imageSize / 2;
        const y = centerY + radius * Math.sin(angle) - imageSize / 2;

        return (
          <Avatar
            key={member.user_id || index}
            className="absolute ring-2 ring-ui-background"
            style={{
              width: `${imageSize}px`,
              height: `${imageSize}px`,
              left: `${x}px`,
              top: `${y}px`,
              zIndex,
            }}
          >
            <AvatarImage
              src={pfpUrl}
              alt={member.name || ""}
              onError={(e) => {
                e.target.src = defaultPfpUrl;
              }}
            />
            <AvatarFallback className="text-[10px]">
              {(member.name || "?").slice(0, 1).toUpperCase()}
            </AvatarFallback>
          </Avatar>
        );
      })}
    </div>
  );
}

/**
 * Single chat row in the sidebar list.
 * @param {{
 *   userId: number,
 *   chat: object,
 *   isSelected: boolean,
 *   onChatSelect?: (chat: object) => void,
 *   onChatRead?: (chatId: number) => void,
 *   highlightQuery?: string,
 * }} props
 * @returns {JSX.Element}
 */
function ChatItem({ userId, chat, isSelected, onChatSelect, onChatRead, highlightQuery = "", now = new Date() }) {
  const navigate = useNavigate();
  const { presenceByUserId } = useUserRealtime();
  const unreadCount = Number(chat.unread_count) || 0;
  const relativeTime = formatRelativeTimestamp(
    chat.last_message_timestamp || chat.last_message_at
  );
  const isGroup = chat.chat_type === "group";
  const dmPeer = !isGroup && Array.isArray(chat.other_members)
    ? chat.other_members[0]
    : null;
  const dmOnline = Boolean(
    dmPeer && isUserOnline(memberLastOnline(dmPeer, presenceByUserId), now)
  );

  async function handleClick() {
    if (onChatSelect) {
      onChatSelect(chat);
    } else {
      navigate(`/messaging/${chat.id}`);
    }
    // Optimistic clear; parent list is source of truth for the badge.
    onChatRead?.(chat.id);
    await readAllChatMessages(chat.id, userId);
  }

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={handleClick}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          handleClick();
        }
      }}
      className={cn(
        "flex cursor-pointer items-center rounded-md p-3 transition-colors hover:bg-chat-list-row-hover",
        isSelected && "border-l-4 border-accent bg-chat-list-row-selected-bg"
      )}
    >
      {isGroup ? (
        <GroupChatPfp chat={chat} n={3} />
      ) : (
        <div className="relative mr-3 h-12 w-12 shrink-0">
          <Avatar className="h-12 w-12">
            <AvatarImage
              src={chat.image_url || defaultPfpUrl}
              alt={chat.name}
              onError={(e) => {
                e.target.src = defaultPfpUrl;
              }}
            />
            <AvatarFallback className="text-sm">
              {chat.name?.slice(0, 2)?.toUpperCase() || "?"}
            </AvatarFallback>
          </Avatar>
          {dmOnline ? (
            <span
              className={cn(
                "absolute bottom-0 right-0 h-3.5 w-3.5 rounded-full bg-presence-online ring-2",
                isSelected ? "ring-chat-list-row-selected-bg" : "ring-secondary"
              )}
              aria-label="Active now"
            />
          ) : null}
        </div>
      )}
      <div className="w-0 flex-1 overflow-hidden">
        <div className="flex items-center justify-between gap-2">
          <div className="max-w-full truncate font-semibold text-ui-foreground">
            {highlightQuery.trim() ? (
              <HighlightedText text={chat.name || ""} query={highlightQuery} />
            ) : (
              chat.name
            )}
          </div>
          {unreadCount > 0 && (
            <Badge variant="default" className="ml-2 shrink-0 px-2 py-0.5">
              {unreadCount}
            </Badge>
          )}
        </div>
        {chat.last_message_content && (
          <div className="mt-1 flex w-full min-w-0 items-baseline gap-1 text-sm text-ui-muted-foreground">
            <span className="min-w-0 flex-1 truncate">
              {chat.last_message_content}
            </span>
            {relativeTime ? (
              <span className="shrink-0 text-ui-muted-foreground">
                {relativeTime}
              </span>
            ) : null}
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * Loading placeholders for the chat list.
 * @returns {JSX.Element}
 */
function ChatListSkeleton() {
  return (
    <div className="space-y-3 p-4" aria-hidden="true">
      {Array.from({ length: 6 }).map((_, i) => (
        <div key={i} className="flex items-center gap-3">
          <Skeleton className="h-12 w-12 shrink-0 rounded-full" />
          <div className="min-w-0 flex-1 space-y-2">
            <Skeleton className="h-4 w-2/3 max-w-[12rem]" />
            <Skeleton className="h-3 w-full max-w-[14rem]" />
          </div>
        </div>
      ))}
    </div>
  );
}

/**
 * Sidebar list of conversations.
 * @param {{
 *   userId?: number,
 *   chats: Array<object>,
 *   loading: boolean,
 *   selectedChat?: object | null,
 *   onChatSelect?: (chat: object) => void,
 *   onChatRead?: (chatId: number) => void,
 *   emptyLabel?: string,
 * }} props
 * @returns {JSX.Element}
 */
function ChatList({
  userId,
  chats,
  loading,
  selectedChat,
  onChatSelect,
  onChatRead,
  emptyLabel = "No chats found.",
}) {
  const { seedPresenceFromChats } = useUserRealtime();
  const now = useNow();

  useEffect(() => {
    seedPresenceFromChats(chats);
  }, [chats, seedPresenceFromChats]);
  if (!userId) {
    return (
      <div className="p-4 text-ui-muted-foreground">
        Please log in to view your chats.
      </div>
    );
  }

  if (loading) {
    return <ChatListSkeleton />;
  }

  if (chats.length === 0) {
    return (
      <div className="p-4 text-sm text-ui-muted-foreground">{emptyLabel}</div>
    );
  }

  return (
    <ScrollArea className="h-full max-h-full min-h-0 w-full">
      <div className="space-y-0.5 p-2 pr-2">
        {chats.map((chat) => (
          <ChatItem
            key={chat.id}
            userId={userId}
            chat={chat}
            isSelected={selectedChat && selectedChat.id === chat.id}
            onChatSelect={onChatSelect}
            onChatRead={onChatRead}
            now={now}
          />
        ))}
      </div>
    </ScrollArea>
  );
}

export { ChatItem, ChatList };
